import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Dashboard, PageLayout, TabPills } from '@components/common';
import { useActiveTab } from '@components/hooks/useActiveTab';
import { BackButton, HeadContent } from '@fourthwall/components';
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider';
import { routing } from '@utils/routing';
import * as S from './PlatformAppEdit.styled';
import { usePlatformAppClient } from './hooks/usePlatformAppClient';
import { AdminTab } from './modules/AdminTab';
import { BasicInformationTab } from './modules/BasicInformationTab/BasicInformationTab';
import { OAuthTab } from './modules/OAuthTab/OAuthTab';
import { ScopesTab } from './modules/ScopesTab/ScopesTab';
import { SectionsTab } from './modules/SectionsTab/SectionsTab';
export const PlatformAppEdit = () => {
    const { appId } = useParams();
    const { data: platformAppClient, isLoading } = usePlatformAppClient(appId || '');
    const adminMode = useAdminModeContext();
    const isAdmin = !!adminMode?.isActive;
    const TABS = [
        { label: 'Basic Information', value: 'basic' },
        { label: 'OAuth', value: 'oauth' },
        { label: 'Scopes', value: 'scopes' },
        { label: 'Sections', value: 'sections' },
        ...(isAdmin ? [{ label: 'Admin', value: 'admin', isAdminOnly: true }] : []),
    ];
    const { activeTab, setTabParam } = useActiveTab({
        tabValues: TABS.map((tab) => tab.value),
    });
    const app = useMemo(() => {
        if (!platformAppClient || !appId)
            return null;
        return {
            ...platformAppClient,
            name: platformAppClient.displayName,
            scopes: platformAppClient.scopes.map((scope) => scope.id),
            sections: platformAppClient.sections.map((section) => ({
                ...section,
                settings: section.settings.map((setting) => ({
                    ...setting,
                    // For drag / drop to work, we need to ensure that there is a unique id for each setting
                    // SR API does not have a unique id for header types
                    id: setting.type === 'header' ? uuidv4() : setting.id,
                })),
            })),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
    }, [platformAppClient, appId]);
    if (isLoading) {
        return (React.createElement(React.Fragment, null,
            React.createElement(HeadContent, { title: "Loading App..." }),
            React.createElement("div", null, "Loading...")));
    }
    if (!app) {
        return (React.createElement(React.Fragment, null,
            React.createElement(HeadContent, { title: "App Not Found" }),
            React.createElement("div", null, "App not found")));
    }
    return (React.createElement(PageLayout, null,
        React.createElement(Dashboard.Section, { name: `${app.name}`, topContent: React.createElement(BackButton, { label: "Back", to: routing.settings.platformApps.self }) },
            React.createElement(TabPills, { options: TABS, value: activeTab, onChange: setTabParam }),
            React.createElement(S.TabContent, null,
                activeTab === 'basic' && React.createElement(BasicInformationTab, { app: app }),
                activeTab === 'oauth' && React.createElement(OAuthTab, { app: app }),
                activeTab === 'scopes' && React.createElement(ScopesTab, { app: app }),
                activeTab === 'sections' && React.createElement(SectionsTab, { app: app }),
                activeTab === 'admin' && isAdmin && React.createElement(AdminTab, { app: app })))));
};
